import { useEffect } from 'react';
import { useWebSocket } from '../context/WebSocketContextUtil';
import { WebSocketEventType, EventHandler } from '../types/websocket';

/**
 * Hook to subscribe to a specific WebSocket event
 * Automatically cleans up the event listener when component unmounts
 *
 * @param eventType - The event type to listen for
 * @param handler - The handler function to call when the event occurs
 * @param enabled - Whether the subscription is active (default: true)
 *
 * @example
 * ```tsx
 * useWebSocketEvent(
 *   WebSocketEventType.DEVICE_STATUS_CHANGED,
 *   (payload) => {
 *     console.log('Device status changed:', payload);
 *     queryClient.invalidateQueries(['devices']);
 *   }
 * );
 * ```
 */
export function useWebSocketEvent<T = unknown>(
  eventType: WebSocketEventType,
  handler: EventHandler<T>,
  enabled = true
) {
  const { addEventListener } = useWebSocket();

  useEffect(() => {
    if (!enabled) {
      return;
    }

    const unsubscribe = addEventListener(eventType, handler);

    return () => {
      unsubscribe();
    };
  }, [eventType, handler, enabled, addEventListener]);
}
